import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import type { SupportedLocale } from "@/lib/locales";
import { isSupportedLocale } from "@/lib/locales";
import {
  buildAutoTranslateRequest,
  descriptionNeedsAutoTranslate,
} from "@/lib/catalogAutoTranslate";
import { toastApiError } from "@/lib/errorMessages";
import type { TranslationFormValues } from "@/lib/translationResolution";
import { findMissingTranslations } from "@/lib/translationResolution";
import { catalogTranslationService } from "@/services/catalogTranslationService";
import type { AutoTranslateCatalogFieldsResponse } from "@/services/catalogTranslationService";

type UseCatalogAutoTranslateArgs = {
  activityId?: string;
  sourceLanguage: SupportedLocale;
  nameTranslations: TranslationFormValues;
  descriptionTranslations: TranslationFormValues;
  /** When true, descriptions must be complete for every language before translate. */
  requireDescription?: boolean;
  /** Called after confirm-dialog translate. Omit when using `execute` and applying yourself. */
  onApplied?: (result: AutoTranslateCatalogFieldsResponse) => void;
  onMissingName: () => void;
  onMissingDescription?: () => void;
};

export function useCatalogAutoTranslate({
  activityId,
  sourceLanguage,
  nameTranslations,
  descriptionTranslations,
  requireDescription = true,
  onApplied,
  onMissingName,
  onMissingDescription,
}: UseCatalogAutoTranslateArgs) {
  const { t } = useTranslation("components");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executePending, setExecutePending] = useState(false);
  const pendingSourceRef = useRef(sourceLanguage);

  const includeDescription =
    requireDescription || descriptionNeedsAutoTranslate(descriptionTranslations);

  const mutation = useMutation({
    mutationFn: () => {
      const request = buildAutoTranslateRequest(
        pendingSourceRef.current,
        nameTranslations,
        descriptionTranslations,
        { includeDescription },
      );
      return catalogTranslationService.translateCatalogFieldsForContext(
        activityId,
        request,
      );
    },
    onSuccess: (result) => {
      setConfirmOpen(false);
      onApplied?.(result);
      toast.success(t("productItem.autoTranslateApplied"));
    },
    onError: (err) => {
      toastApiError(err, "autoTranslateItem");
    },
  });

  const prepareSource = (
    overrideSourceLanguage?: SupportedLocale,
  ): SupportedLocale | null => {
    const source =
      typeof overrideSourceLanguage === "string" &&
      isSupportedLocale(overrideSourceLanguage)
        ? overrideSourceLanguage
        : sourceLanguage;
    pendingSourceRef.current = source;
    if (!nameTranslations[source]?.trim()) {
      onMissingName();
      return null;
    }
    if (
      requireDescription &&
      !descriptionTranslations[source]?.trim()
    ) {
      (onMissingDescription ?? onMissingName)();
      return null;
    }
    return source;
  };

  const run = (overrideSourceLanguage?: SupportedLocale) => {
    if (prepareSource(overrideSourceLanguage) == null) return;
    setConfirmOpen(true);
  };

  /**
   * Validate source and translate immediately (no confirm dialog).
   * Returns the API result so the caller can apply + continue (e.g. save)
   * without going through `onApplied`.
   */
  const execute = async (
    overrideSourceLanguage?: SupportedLocale,
  ): Promise<AutoTranslateCatalogFieldsResponse | null> => {
    if (prepareSource(overrideSourceLanguage) == null) return null;
    setExecutePending(true);
    try {
      const request = buildAutoTranslateRequest(
        pendingSourceRef.current,
        nameTranslations,
        descriptionTranslations,
        { includeDescription },
      );
      const result =
        await catalogTranslationService.translateCatalogFieldsForContext(
          activityId,
          request,
        );
      toast.success(t("productItem.autoTranslateApplied"));
      return result;
    } catch (err) {
      toastApiError(err, "autoTranslateItem");
      return null;
    } finally {
      setExecutePending(false);
    }
  };

  const nameMissing = findMissingTranslations(nameTranslations).length > 0;
  const descriptionMissing =
    includeDescription &&
    findMissingTranslations(descriptionTranslations).length > 0;
  const refreshingExisting = !nameMissing && !descriptionMissing;

  return {
    run,
    execute,
    isPending: mutation.isPending || executePending,
    confirmOpen,
    confirmTitle: refreshingExisting
      ? t("productItem.autoTranslateRefreshTitle")
      : t("productItem.autoTranslateConfirmTitle"),
    confirmMessage: refreshingExisting
      ? t("productItem.autoTranslateRefreshConfirm")
      : t("productItem.autoTranslateConfirm"),
    onConfirmClose: () => {
      if (!mutation.isPending) setConfirmOpen(false);
    },
    onConfirm: () => mutation.mutate(),
  };
}

export { applyAutoTranslateResult } from "@/lib/catalogAutoTranslate";
